import {computed, type ComputedRef} from 'vue';
import {until} from '@vueuse/core';
import {defineStore} from 'pinia';
import {useFeatureFlagQuery} from '@/api/queries/queries/useFeatureFlagQuery';
import {type LicensedFeatureFlag} from '@/api/models/featureFlags/featureFlagModel';
import type {FeatureFlagName} from '@/api/models/featureFlags/featureFlagName';
import type {ValidLicense} from '@deskly/shared-ui/licensing';

export const useFeatureFlagStore = defineStore('featureFlags', () => {
    const {useFeatureFlag} = useHierarchicalFeatureFlags();

    const sendNotifications = useFeatureFlag('send-notifications');
    const receiveNotifications = useFeatureFlag('receive-notifications');
    const notificationPreferences = useFeatureFlag('notification-preferences');

    const oauth = useFeatureFlag('oauth');

    //booking flags
    const bookingFilter = useFeatureFlag('booking-filter');
    const bookingPresentUsers = useFeatureFlag('booking-present-users');
    const bookingResourceList = useFeatureFlag('booking-resource-list');
    const bookingDetailPage = useFeatureFlag('booking-detail-page');
    const bookingForOthers = useFeatureFlag('booking-for-others');
    const waitingList = useFeatureFlag('waiting-list');
    const bookingCalendarCollapse = useFeatureFlag('booking-calendar-collapse');
    const bookingComments = useFeatureFlag('booking-comments');
    const bookingCommentSearch = useFeatureFlag('booking-comment-search');
    const bookingSpaceAvailabilities = useFeatureFlag('booking-space-availabilities');

    const favoriteSeatsPage = useFeatureFlag('favorite-seats-page');
    const iloq = useFeatureFlag('iloq');
    const kioskMode = useFeatureFlag('kiosk-mode');
    const kioskModeCheckIn = useFeatureFlag('kiosk-mode-check-in');
    const newKioskMode = useFeatureFlag('new-kiosk-mode');

    const calendarUserSubscriptions = useFeatureFlag('calendar-user-subscriptions');
    const outlookCalendarSync = useFeatureFlag('calendar-sync');
    const delegatedCalendarAccess = useFeatureFlag('delegated-calendar-access');
    const calendarUiRefresh = useFeatureFlag('calendar-ui-refresh');
    const restApi = useFeatureFlag('rest-api');
    const mercure = useFeatureFlag('mercure');
    const mqttIntegration = useFeatureFlag('mqtt-integration');
    const desklyConnect = useFeatureFlag('deskly-connect');
    const desklyConnectAutoBooking = useFeatureFlag('deskly-connect-auto-booking');
    const desklyConnectAutoBookingCategorical = useFeatureFlag(
        'deskly-connect-auto-booking-categorical'
    );
    const desklyConnectCheckInOnly = useFeatureFlag('deskly-connect-check-in-only');
    const desklyConnectLiveStatus = useFeatureFlag('deskly-connect-live-status');
    const desklyConnectUsageStatistics = useFeatureFlag('deskly-connect-usage-statistics');
    const auditLogAccess = useFeatureFlag('audit-log-access');
    const weekOverview = useFeatureFlag('week-overview');
    const celloReferralAccess = useFeatureFlag('cello');
    const costCenter = useFeatureFlag('cost-center');
    const heatmapsAccess = useFeatureFlag('heatmaps');
    const bookingRuleRestriction = useFeatureFlag('booking-rule-restrictions');
    const bookingRulePriority = useFeatureFlag('booking-rule-priorities');
    const bookingRulePermanent = useFeatureFlag('booking-rule-permanent');
    const bookingRules = useCombinedAnyFeatureFlags(
        bookingRulePriority,
        bookingRulePermanent,
        bookingRuleRestriction
    );
    const newBookingModal = useFeatureFlag('new-booking-modal');
    const newBookingSidebarDesign = useFeatureFlag('new-booking-sidebar-design');
    const markerSizing = useFeatureFlag('marker-sizing');
    const spaceImages = useFeatureFlag('space-images');
    const parkingSpaceResource = useFeatureFlag('parking-spaces');
    const aiAssistant = useFeatureFlag('ai-assistant');
    const licensePlates = useFeatureFlag('license-plates');
    const userBookingSummary = useFeatureFlag('user-booking-summary');
    const userTags = useFeatureFlag('user-tags');
    const userTagsCustom = useFeatureFlag('user-tags-custom');
    const resourceConversion = useFeatureFlag('resource-conversion');
    const resourceTypeMeetingRoom = useFeatureFlag('resource-type-meeting-room');
    const resourceTypeZone = useFeatureFlag('resource-type-zone');
    const cancelledBooking = useFeatureFlag('cancelled-bookings');
    const licenseSelfService = useFeatureFlag('license-self-service');
    const customTimeframes = useFeatureFlag('custom-timeframes');
    const newUserAdministration = useFeatureFlag('new-user-administration');
    const newUserForm = useFeatureFlag('new-user-form');
    const oidcLogin = useFeatureFlag('oidc-login');
    const selectionInfoBanner = useFeatureFlag('selection-info-banner');
    const analyticsSensorStats = useFeatureFlag('deskly-connect-analytics-sensor-stats');
    const occupancySensorStats = useFeatureFlag('deskly-connect-occupancy-sensor-stats');
    const permissionGroupCheckin = useFeatureFlag('permission-group-checkin');
    const permissionGroups = useFeatureFlag('permission-groups');
    const featurebase = useFeatureFlag('featurebase');
    const featurebaseFeedbackWidget = useFeatureFlag('featurebase-feedback-widget');
    const bookingRealtimeUpdates = useFeatureFlag('booking-realtime-updates');
    const guestBookings = useFeatureFlag('guest-bookings');
    const visitorManagement = useFeatureFlag('visitor-management');
    const registrationMailToFirstAdmin = useFeatureFlag('registration-mail-to-first-admin');
    const subdomainRedirect = useFeatureFlag('subdomain-redirect');
    const newBookingAnonymization = useFeatureFlag('new-booking-anonymization');
    const allAnonymousMode = useFeatureFlag('all-anonymous-mode');
    const superAdminAccess = useFeatureFlag('super-admin-access');
    const teamsMessenger = useFeatureFlag('teams-messenger');
    const hotDesking = useFeatureFlag('hot-desking');
    const catering = useFeatureFlag('catering');
    const whiteLabeling = useFeatureFlag('white-labeling');
    const hubspotCustomEventDefinitions = useFeatureFlag('hubspot-custom-event-definitions');
    const pastBookings = useFeatureFlag('past-bookings');
    const webhooks = useFeatureFlag('webhooks');

    return {
        sendNotifications,
        receiveNotifications,
        notificationPreferences,

        oauth,

        //booking flags
        bookingFilter,
        bookingPresentUsers,
        bookingResourceList,
        bookingDetailPage,
        bookingForOthers,
        waitingList,
        bookingCalendarCollapse,
        bookingSpaceAvailabilities,

        favoriteSeatsPage,
        iloq,
        kioskMode,
        kioskModeCheckIn,
        newKioskMode,
        calendarUserSubscriptions,
        outlookCalendarSync,
        delegatedCalendarAccess,
        calendarUiRefresh,
        restApi,
        mercure,
        mqttIntegration,
        desklyConnect,
        desklyConnectAutoBooking,
        desklyConnectAutoBookingCategorical,
        desklyConnectCheckInOnly,
        desklyConnectLiveStatus,
        desklyConnectUsageStatistics,
        auditLogAccess,
        weekOverview,
        celloReferralAccess,
        costCenter,
        heatmapsAccess,
        bookingRules,
        bookingRuleRestriction,
        bookingRulePriority,
        bookingRulePermanent,
        newBookingModal,
        newBookingSidebarDesign,
        markerSizing,
        spaceImages,
        parkingSpaceResource,
        aiAssistant,
        licensePlates,
        userBookingSummary,
        bookingComments,
        bookingCommentSearch,
        userTags,
        userTagsCustom,
        resourceConversion,
        resourceTypeMeetingRoom,
        resourceTypeZone,
        cancelledBooking,
        licenseSelfService,
        newUserAdministration,
        newUserForm,
        oidcLogin,
        customTimeframes,
        selectionInfoBanner,
        analyticsSensorStats,
        occupancySensorStats,
        permissionGroupCheckin,
        permissionGroups,
        featurebase,
        featurebaseFeedbackWidget,
        bookingRealtimeUpdates,
        guestBookings,
        visitorManagement,
        registrationMailToFirstAdmin,
        subdomainRedirect,
        newBookingAnonymization,
        allAnonymousMode,
        superAdminAccess,
        teamsMessenger,
        hotDesking,
        catering,
        whiteLabeling,
        hubspotCustomEventDefinitions,
        pastBookings,
        webhooks,
    };
});

export type FeatureFlagState = {
    loading: boolean;
    isActive: boolean;
    isReady: () => Promise<boolean>;
    availableInLicenseTiers: ValidLicense[] | null;
};

function useHierarchicalFeatureFlags() {
    const {featureFlagResponse, isLoading} = useFeatureFlagQuery();

    function useFeatureFlag(flagName: FeatureFlagName): ComputedRef<FeatureFlagState> {
        const featureFlag = computed<LicensedFeatureFlag>(
            () => featureFlagResponse.value[flagName] ?? null
        );

        const isActive = computed<boolean>(
            () => !isLoading.value && featureFlag.value !== false && featureFlag.value !== null
        );
        const availableInLicenseTiers = computed<ValidLicense[] | null>(() => {
            const flag = featureFlag.value;
            if (Array.isArray(flag)) {
                return flag;
            }
            return null;
        });

        async function isReady(): Promise<boolean> {
            await until(isLoading).toBe(false);
            return isActive.value;
        }

        return computed<FeatureFlagState>(() => ({
            isActive: isActive.value ?? false,
            loading: isLoading.value,
            isReady,
            availableInLicenseTiers: availableInLicenseTiers.value,
        }));
    }

    return {
        useFeatureFlag,
    };
}

/**
 * Combines multiple feature flags into one, requiring at least one to be active.
 * The resulting feature flag state will hold the lowest license tier to make any available.
 */
function useCombinedAnyFeatureFlags(...featureFlags: ComputedRef<FeatureFlagState>[]) {
    const isActive = computed(() => featureFlags.some(flag => flag.value.isActive));
    const loading = computed(() => featureFlags.some(flag => flag.value.loading));

    const availableInLicenseTiers = computed<ValidLicense[] | null>(() => {
        const licenseTiers = featureFlags
            .map(flag => flag.value.availableInLicenseTiers)
            .filter((tiers): tiers is ValidLicense[] => tiers !== null);

        if (licenseTiers.length !== featureFlags.length) {
            return null;
        }

        return Array.from(new Set(licenseTiers.flat()));
    });

    return computed<FeatureFlagState>(() => ({
        isActive: isActive.value,
        loading: loading.value,
        isReady: async () => {
            await until(loading).toBe(false);
            return isActive.value;
        },
        availableInLicenseTiers: availableInLicenseTiers.value,
    }));
}
